import { router } from '@inertiajs/react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import DriveSyncLayout from '../../layouts/DriveSyncLayout';
import CarriersHeader from './CarriersHeader';

export default function New({ errors = {} }) {
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    cnpj: '',
    phone: '',
    geolocation: false,
    geolocation_interval: 15
  });
  const [loading, setLoading] = React.useState(false);
  const [formErrors, setFormErrors] = React.useState(errors);

  // Sync server errors with local state when props change
  React.useEffect(() => {
    if (errors && Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setLoading(false);
    }
  }, [errors]);

  const getValidErrors = () => {
    return Object.values(formErrors).filter(error => error && error.trim());
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing (both client-side and server errors)
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (formData.cnpj && !/^\d{14}$/.test(formData.cnpj.replace(/\D/g, ''))) {
      newErrors.cnpj = 'CNPJ deve ter 14 dígitos';
    }

    if (formData.geolocation && (!formData.geolocation_interval || formData.geolocation_interval < 1)) {
      newErrors.geolocation_interval = 'Intervalo deve ser maior que 0';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      router.post('/carriers', {
        carrier: formData
      }, {
        preserveScroll: true,
        onError: (errors) => {
          setFormErrors(errors);
        },
        onFinish: () => {
          setLoading(false);
        }
      });
    } catch (err) {
      setLoading(false);
    }
  };

  const handleBack = () => {
    router.visit('/carriers');
  };

  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    }
    return numbers.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  };

  const handleCNPJChange = (event) => {
    const value = event.target.value.replace(/\D/g, '');
    setFormData(prev => ({ ...prev, cnpj: value }));
  };

  const handlePhoneChange = (event) => {
    const value = event.target.value.replace(/\D/g, '');
    setFormData(prev => ({ ...prev, phone: value }));
  };

  return (
    <DriveSyncLayout>
      <CarriersHeader />

      <Box sx={{
        width: '100%',
        maxWidth: { sm: '100%', md: '800px' },
        mx: 'auto',
        mt: 2
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBack} sx={{ mr: 2 }} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Nova Transportadora
          </Typography>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Cadastre uma nova transportadora para gerenciar motoristas e viagens.
        </Typography>

        {/* Error Alert */}
        {getValidErrors().length > 0 && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body2" component="div">
              <strong>Foram encontrados {getValidErrors().length} erro(s):</strong>
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              {getValidErrors().map((error, index) => (
                <Box component="li" key={index}>
                  <Typography variant="body2">{error}</Typography>
                </Box>
              ))}
            </Box>
          </Alert>
        )}

        {/* Form */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <BusinessIcon color="primary" />
              <Typography variant="h6">
                Informações da Transportadora
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  id="name"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  placeholder="Nome da transportadora"
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  disabled={loading}
                  variant="outlined"
                  fullWidth
                />

                <TextField
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  placeholder="contato@transportadora.com"
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                  disabled={loading}
                  variant="outlined"
                  fullWidth
                />

                <TextField
                  id="cnpj"
                  value={formatCNPJ(formData.cnpj)}
                  onChange={handleCNPJChange}
                  placeholder="00.000.000/0000-00"
                  error={!!formErrors.cnpj}
                  helperText={formErrors.cnpj}
                  disabled={loading}
                  variant="outlined"
                  fullWidth
                  inputProps={{ maxLength: 18 }}
                />

                <TextField
                  id="phone"
                  value={formatPhone(formData.phone)}
                  onChange={handlePhoneChange}
                  placeholder="(00) 00000-0000"
                  error={!!formErrors.phone}
                  helperText={formErrors.phone}
                  disabled={loading}
                  variant="outlined"
                  fullWidth
                  inputProps={{ maxLength: 15 }}
                />

                {/* Geolocation Section */}
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <LocationOnIcon color="primary" />
                    <Typography variant="h6">
                      Configurações de Geolocalização
                    </Typography>
                  </Box>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.geolocation}
                        onChange={handleInputChange('geolocation')}
                        disabled={loading}
                      />
                    }
                    label="Habilitar geolocalização automática"
                  />

                  {formData.geolocation && (
                    <Box sx={{ mt: 2 }}>
                      <FormControl variant="outlined" fullWidth error={!!formErrors.geolocation_interval}>
                        <Select
                          value={formData.geolocation_interval}
                          onChange={handleInputChange('geolocation_interval')}
                          label="Intervalo de Atualização"
                          disabled={loading}
                        >
                          <MenuItem value={5}>5 minutos</MenuItem>
                          <MenuItem value={10}>10 minutos</MenuItem>
                          <MenuItem value={15}>15 minutos</MenuItem>
                          <MenuItem value={30}>30 minutos</MenuItem>
                          <MenuItem value={60}>1 hora</MenuItem>
                        </Select>
                        {formErrors.geolocation_interval && (
                          <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                            {formErrors.geolocation_interval}
                          </Typography>
                        )}
                      </FormControl>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <BusinessIcon />}
          >
            {loading ? 'Criando...' : 'Criar Transportadora'}
          </Button>
        </Box>
      </Box>
    </DriveSyncLayout>
  );
} 